/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { FC } from 'react';
import { ReportExport } from './types/report-export';
import { MetricTypes } from 'common/constants/metric-types';
import { getMetricTypeFriendlyName } from './functions/metric-type-functions';
import { getSymbolFromCurrency } from 'features/currency/currencySymbolMapHelper';
import CSVExportButton from 'features/csv-export/csv-export-button';
import moment from 'moment';
import { MetricValue } from './types/metric-report';

function formatToEnGbNumberWithDecimalPlaces(
  value: number | string | undefined,
  minimumDecimalPlaces: number,
  maximumDecimalPlaces: number
): string {
  if (value === undefined || value === null) {
    return '0';
  }

  let valueAsNumber = +value;

  if (isNaN(valueAsNumber)) {
    valueAsNumber = 0;
  }

  return Intl.NumberFormat('en-GB', {
    minimumFractionDigits: minimumDecimalPlaces,
    maximumFractionDigits: maximumDecimalPlaces
  }).format(valueAsNumber);
}

function formatToCurrencyCode(
  value: number | string | undefined,
  currencyCode: string
): string {
  if (value === undefined) {
    return `${currencyCode}0.00`;
  }

  const formattedValue = Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(+value);

  return `${currencyCode}${formattedValue}`;
}

interface ReportsCSVDownloadButtonProps {
  reportExport: ReportExport;
}
/*
  This exists in the front end as it's easier to replicate what's going on in the table view
  We also save on unnecessary Athena calls
  We could tie the column configuration to export but it makes things more complex to understand so we went with an easy to read switch block
*/
export const ReportsCSVExportButton: FC<ReportsCSVDownloadButtonProps> = ({
  reportExport
}) => {
  const startDateShortIso = moment(reportExport.dates.startDate).format(
    'yyyy-MM-DD'
  );
  const endDateShortIso = moment(reportExport.dates.endDate).format(
    'yyyy-MM-DD'
  );
  const searchTermFileNameSuffix = reportExport.searchTerm
    ? `_${reportExport.searchTerm}`
    : '';

  const filename = `Reports_${startDateShortIso}_${endDateShortIso}_${reportExport.attributionModel}${searchTermFileNameSuffix}`;

  const currencySymbol = getSymbolFromCurrency(reportExport.currencyCode);

  const orderedHeaders = reportExport.orderedReportColumnIds;

  const searchTerm = reportExport.searchTerm
    ? reportExport.searchTerm.toUpperCase()
    : null;

  const orderedRows = reportExport.metricReports
    .filter((metric) =>
      searchTerm ? metric.Field.toUpperCase().includes(searchTerm) : metric
    )
    .map((metric) =>
      orderedHeaders.map((header) => {
        switch (header) {
          case MetricTypes.CLICKS:
          case MetricTypes.IMPRESSIONS:
          case MetricTypes.INBOUND_CALL:
          case MetricTypes.WEB:
          case MetricTypes.OPPORTUNITIES:
          case MetricTypes.REVENUES:
            return formatToEnGbNumberWithDecimalPlaces(
              (metric[header] as MetricValue).main,
              0,
              2
            );
          case MetricTypes.OPPORTUNITY_VALUES:
          case MetricTypes.REVENUE_VALUES:
          case MetricTypes.COST:
          case MetricTypes.CPA:
          case MetricTypes.CPS:
          case MetricTypes.ROI:
            return formatToCurrencyCode(
              (metric[header] as MetricValue).main,
              currencySymbol
            );
          case MetricTypes.ROAS:
            return formatToEnGbNumberWithDecimalPlaces(
              (metric[header] as MetricValue).main,
              2,
              2
            );
          case 'Field':
            return metric[header];
          default:
            throw Error(
              `Unexpected MetricType/ColumnName found when trying to create ReportsCsvExportButton: ${header}`
            );
        }
      })
    );

  const friendlyOrderedHeaders = orderedHeaders.map((header) =>
    getMetricTypeFriendlyName(header)
  );

  return orderedRows.length > 0 ? (
    <CSVExportButton
      headers={friendlyOrderedHeaders}
      rows={orderedRows}
      filename={filename}
    />
  ) : (
    <></>
  );
};

export default ReportsCSVExportButton;
